// ./src/utils/api/InventoryTxnService.ts
//
// Inventory-ledger service — the read-only movement history (InventoryTxn) plus the
// six stock OPERATIONS (receive / transfer / return / adjust / count / consume) and
// the balances read (inventoryBalances). OpResult-native, event-scoped via
// contextEventId. Each operation returns an InventoryTxnResult carrying the created
// ledger rows. A location tuple is { eventId?, venueId?, venueAddressId? }: eventId
// set = that event's stock; eventId null = a home/account location. See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";
import { InventoryItem, InventoryRef } from "./InventoryItemService";

// =============================================================================
// Enum-like constants (mirror the eventsmagic-api boundary/inventory.ts)
// =============================================================================

export const INVENTORY_TXN_OP = {
  RECEIVE: "receive",
  TRANSFER: "transfer",
  RETURN: "return",
  ADJUST: "adjust",
  COUNT: "count",
  CONSUME: "consume",
} as const;

export const INVENTORY_TXN_OP_VALUES = Object.values(INVENTORY_TXN_OP);

export const INVENTORY_TXN_OP_LABELS: Record<string, string> = {
  receive: "Receive",
  transfer: "Transfer",
  return: "Return",
  adjust: "Adjust",
  count: "Count",
  consume: "Consume",
};

export const INVENTORY_TXN_SOURCE = {
  MANUAL: "manual",
  SCAN: "scan",
  REDEMPTION: "redemption",
  TALLY: "tally",
} as const;

export const INVENTORY_TXN_SOURCE_VALUES = Object.values(INVENTORY_TXN_SOURCE);

export const INVENTORY_TXN_SOURCE_LABELS: Record<string, string> = {
  manual: "Manual",
  scan: "Scan",
  redemption: "Redemption",
  tally: "Tally",
};

export const INVENTORY_ADJUST_REASON = {
  BREAKAGE: "breakage",
  DAMAGE: "damage",
  SPOILAGE: "spoilage",
  EXPIRED: "expired",
  LOSS: "loss",
  FOUND: "found",
  CORRECTION: "correction",
  OTHER: "other",
} as const;

export const INVENTORY_ADJUST_REASON_VALUES = Object.values(INVENTORY_ADJUST_REASON);

export const INVENTORY_ADJUST_REASON_LABELS: Record<string, string> = {
  breakage: "Breakage",
  damage: "Damage",
  spoilage: "Spoilage",
  expired: "Expired",
  loss: "Loss",
  found: "Found",
  correction: "Correction",
  other: "Other",
};

// =============================================================================
// GraphQL
// =============================================================================

const INVENTORY_TXN_FIELDS = `
  id
  accountId
  inventoryItemId
  item { id name sku unit currencyCode }
  op
  qtyDelta
  packUnits
  eventId
  event { id name }
  venueId
  venue { id name }
  venueAddressId
  expiresAt
  unitCost
  currencyCode
  vendorId
  vendorLabel
  vendor { id name }
  countedQty
  couponRedemptionId
  tallyEntryId
  reason
  txnGroupId
  scannedCode
  source
  notes
  createdAt
  createdBy
  createdByName
`;

const INVENTORY_TXN_LIST_QUERY = gql`
  query InventoryTxnList($filter: InventoryTxnFilter, $params: PaginationAndSorting) {
    inventoryTxnList(filter: $filter, params: $params) {
      code
      total
      data { ${INVENTORY_TXN_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const INVENTORY_BALANCES_QUERY = gql`
  query InventoryBalances($filter: InventoryBalanceFilter) {
    inventoryBalances(filter: $filter) {
      code
      data {
        inventoryItemId
        item { id name unit }
        eventId
        event { id name }
        venueId
        venue { id name }
        venueAddressId
        expiresAt
        tier
        onHand
        value
        lowStock
      }
      errors { name errors warnings }
    }
  }
`;

const INVENTORY_RESULT = `
  code
  data { ${INVENTORY_TXN_FIELDS} }
  errors { name errors warnings }
`;

const INVENTORY_RECEIVE_MUTATION = gql`
  mutation InventoryReceive($params: InventoryReceiveInput) {
    inventoryReceive(params: $params) { ${INVENTORY_RESULT} }
  }
`;

const INVENTORY_TRANSFER_MUTATION = gql`
  mutation InventoryTransfer($params: InventoryMoveInput) {
    inventoryTransfer(params: $params) { ${INVENTORY_RESULT} }
  }
`;

const INVENTORY_RETURN_MUTATION = gql`
  mutation InventoryReturn($params: InventoryMoveInput) {
    inventoryReturn(params: $params) { ${INVENTORY_RESULT} }
  }
`;

const INVENTORY_ADJUST_MUTATION = gql`
  mutation InventoryAdjust($params: InventoryAdjustInput) {
    inventoryAdjust(params: $params) { ${INVENTORY_RESULT} }
  }
`;

const INVENTORY_COUNT_MUTATION = gql`
  mutation InventoryCount($params: InventoryCountInput) {
    inventoryCount(params: $params) { ${INVENTORY_RESULT} }
  }
`;

const INVENTORY_CONSUME_MUTATION = gql`
  mutation InventoryConsume($params: InventoryConsumeInput) {
    inventoryConsume(params: $params) { ${INVENTORY_RESULT} }
  }
`;

// Write off an item's stock — records an ADJUST per non-zero (location, lot) that
// brings on-hand to 0, in one transaction. Append-only safe.
const INVENTORY_WRITE_OFF_MUTATION = gql`
  mutation InventoryWriteOff($params: InventoryWriteOffInput) {
    inventoryWriteOff(params: $params) { ${INVENTORY_RESULT} }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api InventoryTxn schema)
// =============================================================================

export interface InventoryTxn {
  id?: string;
  accountId?: string | null;
  inventoryItemId?: string | null;
  item?: InventoryItem | null;
  op?: string | null;
  /** Signed change to on-hand (+ received/found, − consumed/transferred out). */
  qtyDelta?: number | null;
  /** The pack size applied to this row (nullable); qtyDelta is in stock UNITS. */
  packUnits?: number | null;
  eventId?: string | null;
  event?: InventoryRef | null;
  venueId?: string | null;
  venue?: InventoryRef | null;
  venueAddressId?: string | null;
  expiresAt?: string | null;
  unitCost?: number | null;
  currencyCode?: string | null;
  vendorId?: string | null;
  vendorLabel?: string | null;
  vendor?: InventoryRef | null;
  countedQty?: number | null;
  couponRedemptionId?: string | null;
  tallyEntryId?: string | null;
  reason?: string | null;
  txnGroupId?: string | null;
  scannedCode?: string | null;
  source?: string | null;
  notes?: string | null;
  createdAt?: string;
  createdBy?: string | null;
  /** Best-effort display name of who recorded the row (via ms_auth); may be null. */
  createdByName?: string | null;
}

/** A location tuple. eventId set → that event's stock; eventId null → a home location. */
export interface InventoryLocationInput {
  eventId?: string | null;
  venueId?: string | null;
  venueAddressId?: string | null;
}

export interface InventoryTxnFilter {
  id?: string[] | null;
  inventoryItemId?: string[] | null;
  eventId?: string[] | null;
  op?: string[] | null;
  txnGroupId?: string[] | null;
  status?: number[] | null;
  /** Full-text: item name/sku + scanned code + notes + vendor (server-side). */
  searchKeyword?: string | null;
  contextEventId?: string | null;
}

export interface InventoryBalancesFilter {
  inventoryItemId?: string | null;
  eventId?: string | null;
  /** Restrict to home/account locations. */
  homeOnly?: boolean | null;
  /** Include lots that have zeroed out. */
  includeZero?: boolean | null;
  contextEventId?: string | null;
}

// ── Operation inputs ──

export interface InventoryReceiveInput {
  contextEventId?: string | null;
  itemId: string;
  /** When packUnits > 1, this is a count of PACKS (backend multiplies by packUnits). */
  quantity: number;
  location?: InventoryLocationInput | null;
  expiresAt?: string | null;
  /** Per-PACK cost when packUnits > 1 (backend divides by packUnits). */
  unitCost?: number | null;
  currencyCode?: string | null;
  vendorId?: string | null;
  scannedCode?: string | null;
  /** Stock units one pack represents (from the scanned barcode). Omit/null for units mode. */
  packUnits?: number | null;
  note?: string | null;
}

export interface InventoryTransferInput {
  contextEventId?: string | null;
  itemId: string;
  quantity: number;
  from?: InventoryLocationInput | null;
  to?: InventoryLocationInput | null;
  /** Optional lot pin; omit for automatic FEFO. */
  expiresAt?: string | null;
  scannedCode?: string | null;
  /** Stock units one pack represents (from the scanned barcode). Omit/null for units mode. */
  packUnits?: number | null;
  note?: string | null;
}

export interface InventoryAdjustInput {
  contextEventId?: string | null;
  itemId: string;
  /** Signed delta (+ found, − breakage/loss). When packUnits > 1, a count of PACKS. */
  quantityDelta: number;
  location?: InventoryLocationInput | null;
  expiresAt?: string | null;
  reason?: string | null;
  scannedCode?: string | null;
  /** Stock units one pack represents (from the scanned barcode). Omit/null for units mode. */
  packUnits?: number | null;
  note?: string | null;
}

export interface InventoryCountInput {
  contextEventId?: string | null;
  itemId: string;
  /** When packUnits > 1, a count of PACKS (backend multiplies by packUnits). */
  countedQuantity: number;
  location?: InventoryLocationInput | null;
  expiresAt?: string | null;
  /** Reason a mismatch is corrected — used only when the count differs from the system on-hand. Default correction. */
  reason?: string | null;
  scannedCode?: string | null;
  /** Stock units one pack represents (from the scanned barcode). Omit/null for units mode. */
  packUnits?: number | null;
  note?: string | null;
}

export interface InventoryConsumeInput {
  contextEventId?: string | null;
  itemId: string;
  quantity: number;
  location?: InventoryLocationInput | null;
  /** Optional lot pin; omit for automatic FEFO. */
  expiresAt?: string | null;
  couponRedemptionId?: string | null;
  scannedCode?: string | null;
  /** Stock units one pack represents (from the scanned barcode). Omit/null for units mode. */
  packUnits?: number | null;
  note?: string | null;
}

export interface InventoryWriteOffInput {
  contextEventId?: string | null;
  itemId: string;
  /** Scope to one event's stock. */
  eventId?: string | null;
  /** Scope to home/account stock only. */
  homeOnly?: boolean | null;
  /** Why the stock is being written off (loss / breakage / correction / other). */
  reason?: string | null;
  note?: string | null;
}

// Re-export the balance shape (defined alongside the item) for convenience.
export type { InventoryBalance } from "./InventoryItemService";

// =============================================================================
// Service
// =============================================================================

export class InventoryTxnService {
  static async list(
    filter?: InventoryTxnFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(INVENTORY_TXN_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async balances(filter: InventoryBalancesFilter): Promise<OpResult> {
    return ApiService.executeQuery(INVENTORY_BALANCES_QUERY, { filter });
  }

  static async receive(params: InventoryReceiveInput): Promise<OpResult> {
    return ApiService.executeMutation(INVENTORY_RECEIVE_MUTATION, { params });
  }

  static async transfer(params: InventoryTransferInput): Promise<OpResult> {
    return ApiService.executeMutation(INVENTORY_TRANSFER_MUTATION, { params });
  }

  static async return(params: InventoryTransferInput): Promise<OpResult> {
    return ApiService.executeMutation(INVENTORY_RETURN_MUTATION, { params });
  }

  static async adjust(params: InventoryAdjustInput): Promise<OpResult> {
    return ApiService.executeMutation(INVENTORY_ADJUST_MUTATION, { params });
  }

  static async count(params: InventoryCountInput): Promise<OpResult> {
    return ApiService.executeMutation(INVENTORY_COUNT_MUTATION, { params });
  }

  static async consume(params: InventoryConsumeInput): Promise<OpResult> {
    return ApiService.executeMutation(INVENTORY_CONSUME_MUTATION, { params });
  }

  static async writeOff(params: InventoryWriteOffInput): Promise<OpResult> {
    return ApiService.executeMutation(INVENTORY_WRITE_OFF_MUTATION, { params });
  }
}
